import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  EyeIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  NoSymbolIcon,
  XCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { emailLogsAPI } from '../services/api';

const initialFilters = {
  search: '',
  status: '',
  templateKey: '',
  actorType: '',
  startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
  endDate: moment().format('YYYY-MM-DD'),
  page: 1,
  limit: 25
};

function EmailLogs() {
  const [logs, setLogs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [summary, setSummary] = useState({ total: 0, sent: 0, failed: 0, disabled: 0 });
  const [filters, setFilters] = useState(initialFilters);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalCount: 0 });
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadEmailLogs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await emailLogsAPI.getLogs(filters);
      setLogs(response.data.logs || []);
      setTemplates(response.data.templates || []);
      setSummary(response.data.summary || { total: 0, sent: 0, failed: 0, disabled: 0 });
      setPagination({
        currentPage: response.data.currentPage || 1,
        totalPages: response.data.totalPages || 1,
        totalCount: response.data.totalCount || 0
      });
      setMessage(response.data.message || '');
    } catch (error) {
      console.error('Failed to load email logs:', error);
      toast.error(error.response?.data?.error || 'Failed to load email logs');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadEmailLogs();
  }, [loadEmailLogs]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: 1
    }));
  };

  const handlePageChange = (page) => {
    setFilters((prev) => ({
      ...prev,
      page
    }));
  };

  const handleRefresh = () => {
    loadEmailLogs();
    toast.success('Email logs refreshed');
  };

  const clearFilters = () => {
    setFilters(initialFilters);
  };

  const openLogDetail = async (id) => {
    try {
      setDetailLoading(true);
      const response = await emailLogsAPI.getLog(id);
      setSelectedLog(response.data.log);
    } catch (error) {
      console.error('Failed to load email log detail:', error);
      toast.error(error.response?.data?.error || 'Failed to load email details');
    } finally {
      setDetailLoading(false);
    }
  };

  const statusBadgeClasses = useMemo(() => ({
    sent: 'bg-emerald-100 text-emerald-700',
    failed: 'bg-rose-100 text-rose-700',
    disabled: 'bg-amber-100 text-amber-700'
  }), []);

  const renderRecipients = (log) => {
    const recipients = Array.isArray(log.toEmails) ? log.toEmails : [];
    if (recipients.length === 0) return 'No recipients';
    if (recipients.length === 1) return recipients[0];
    return `${recipients[0]} +${recipients.length - 1}`;
  };

  const renderActor = (log) => {
    const name = log.actorName || 'System';
    const email = log.actorEmail || '';
    return email ? `${name} (${email})` : name;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Logs</h1>
          <p className="mt-1 text-sm text-gray-500">
            Review every email sent from the app, including sender context and rendered template snapshots.
          </p>
        </div>
        <button
          onClick={handleRefresh}
          className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
        >
          <ArrowPathIcon className="mr-2 h-5 w-5" />
          Refresh
        </button>
      </div>

      {message && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {message}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
          <div className="flex items-center gap-3">
            <EnvelopeIcon className="h-8 w-8 text-indigo-500" />
            <div>
              <p className="text-sm text-gray-500">Total Emails</p>
              <p className="text-2xl font-semibold text-gray-900">{summary.total || 0}</p>
            </div>
          </div>
        </div>
        <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
          <div className="flex items-center gap-3">
            <CheckCircleIcon className="h-8 w-8 text-emerald-500" />
            <div>
              <p className="text-sm text-gray-500">Sent</p>
              <p className="text-2xl font-semibold text-gray-900">{summary.sent || 0}</p>
            </div>
          </div>
        </div>
        <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
          <div className="flex items-center gap-3">
            <XCircleIcon className="h-8 w-8 text-rose-500" />
            <div>
              <p className="text-sm text-gray-500">Failed</p>
              <p className="text-2xl font-semibold text-gray-900">{summary.failed || 0}</p>
            </div>
          </div>
        </div>
        <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
          <div className="flex items-center gap-3">
            <NoSymbolIcon className="h-8 w-8 text-amber-500" />
            <div>
              <p className="text-sm text-gray-500">Disabled</p>
              <p className="text-2xl font-semibold text-gray-900">{summary.disabled || 0}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <h2 className="text-lg font-medium text-gray-900">Filters</h2>
          </div>
          <button
            onClick={clearFilters}
            className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
          >
            Reset
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Search</label>
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Subject, recipient, sender..."
                className="w-full rounded-lg border border-gray-300 pl-10 pr-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            >
              <option value="">All statuses</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Template</label>
            <select
              value={filters.templateKey}
              onChange={(e) => handleFilterChange('templateKey', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            >
              <option value="">All templates</option>
              {templates.map((template) => (
                <option key={template.templateKey} value={template.templateKey}>
                  {template.templateName} ({template.count})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Sender Type</label>
            <select
              value={filters.actorType}
              onChange={(e) => handleFilterChange('actorType', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            >
              <option value="">All senders</option>
              <option value="admin">Admin</option>
              <option value="user">User</option>
              <option value="system">System</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            />
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-200">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-medium text-gray-900">
            Email History ({pagination.totalCount} records)
          </h2>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <ArrowPathIcon className="h-10 w-10 animate-spin text-indigo-500" />
          </div>
        ) : logs.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-3 text-sm font-semibold text-gray-900">No email logs found</h3>
            <p className="mt-1 text-sm text-gray-500">Adjust the filters or widen the date range.</p>
          </div>
        ) : (
          <>
            <div className="space-y-3 p-4 md:hidden">
              {logs.map((log) => (
                <div key={log.id} className="rounded-xl border border-gray-200 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{log.subject}</p>
                      <p className="mt-1 text-xs text-gray-500">{log.templateName}</p>
                    </div>
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusBadgeClasses[log.status] || 'bg-gray-100 text-gray-700'}`}>
                      {log.status}
                    </span>
                  </div>

                  <div className="mt-3 space-y-2 text-xs text-gray-600">
                    <p><span className="font-medium text-gray-800">To:</span> {renderRecipients(log)}</p>
                    <p><span className="font-medium text-gray-800">By:</span> {renderActor(log)}</p>
                    <p><span className="font-medium text-gray-800">Sent:</span> {moment(log.sentAt).format('MMM DD, YYYY hh:mm A')}</p>
                  </div>

                  <button
                    onClick={() => openLogDetail(log.id)}
                    className="mt-4 inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500"
                  >
                    <EyeIcon className="mr-1.5 h-4 w-4" />
                    View template
                  </button>
                </div>
              ))}
            </div>

            <div className="hidden overflow-x-auto md:block">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Recipients</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Sent By</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Sent At</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Preview</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 align-top">
                        <div className="max-w-sm">
                          <p className="text-sm font-semibold text-gray-900">{log.subject}</p>
                          <p className="mt-1 text-sm text-gray-500">{log.templateName}</p>
                          {log.error && (
                            <p className="mt-2 text-xs text-rose-600">{log.error}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 align-top text-sm text-gray-600">
                        <p>{renderRecipients(log)}</p>
                        {Array.isArray(log.ccEmails) && log.ccEmails.length > 0 && (
                          <p className="mt-1 text-xs text-gray-500">CC: {log.ccEmails.join(', ')}</p>
                        )}
                      </td>
                      <td className="px-6 py-4 align-top text-sm text-gray-600">
                        <p>{log.actorName || 'System'}</p>
                        <p className="mt-1 text-xs text-gray-500">{log.actorEmail || log.actorType}</p>
                      </td>
                      <td className="px-6 py-4 align-top text-sm text-gray-600">
                        <p>{moment(log.sentAt).format('MMM DD, YYYY')}</p>
                        <p className="mt-1 text-xs text-gray-500">{moment(log.sentAt).format('hh:mm A')}</p>
                      </td>
                      <td className="px-6 py-4 align-top">
                        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusBadgeClasses[log.status] || 'bg-gray-100 text-gray-700'}`}>
                          {log.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right align-top">
                        <button
                          onClick={() => openLogDetail(log.id)}
                          className="inline-flex items-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                          <EyeIcon className="mr-1.5 h-4 w-4" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-gray-600">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={pagination.currentPage === 1}
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={pagination.currentPage === pagination.totalPages}
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {(detailLoading || selectedLog) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
          <div className="max-h-[90vh] w-full max-w-6xl overflow-hidden rounded-3xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Email Template Preview</h3>
                <p className="text-sm text-gray-500">
                  {selectedLog?.templateName || 'Loading email details'}
                </p>
              </div>
              <button
                onClick={() => setSelectedLog(null)}
                className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {detailLoading && !selectedLog ? (
              <div className="flex items-center justify-center py-20">
                <ArrowPathIcon className="h-10 w-10 animate-spin text-indigo-500" />
              </div>
            ) : selectedLog && (
              <div className="grid max-h-[calc(90vh-80px)] grid-cols-1 overflow-y-auto lg:grid-cols-[360px_minmax(0,1fr)]">
                <div className="border-b border-gray-200 bg-gray-50 p-6 lg:border-b-0 lg:border-r">
                  <div className="space-y-4 text-sm">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Subject</p>
                      <p className="mt-1 text-gray-900">{selectedLog.subject}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Template</p>
                      <p className="mt-1 text-gray-900">{selectedLog.templateName}</p>
                      <p className="text-xs text-gray-500">{selectedLog.templateKey}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Sent By</p>
                      <p className="mt-1 text-gray-900">{renderActor(selectedLog)}</p>
                      <p className="text-xs text-gray-500">{selectedLog.actorRole || selectedLog.actorType}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">To</p>
                      <p className="mt-1 text-gray-900">{(selectedLog.toEmails || []).join(', ') || 'N/A'}</p>
                    </div>
                    {selectedLog.ccEmails?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">CC</p>
                        <p className="mt-1 text-gray-900">{selectedLog.ccEmails.join(', ')}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Status</p>
                      <span className={`mt-1 inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusBadgeClasses[selectedLog.status] || 'bg-gray-100 text-gray-700'}`}>
                        {selectedLog.status}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Sent At</p>
                      <p className="mt-1 text-gray-900">{moment(selectedLog.sentAt).format('MMM DD, YYYY hh:mm A')}</p>
                    </div>
                    {selectedLog.error && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Error</p>
                        <p className="mt-1 text-rose-600">{selectedLog.error}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-white p-4">
                  {selectedLog.htmlBody ? (
                    <iframe
                      title={`email-preview-${selectedLog.id}`}
                      srcDoc={selectedLog.htmlBody}
                      className="h-[65vh] w-full rounded-2xl border border-gray-200 bg-white"
                    />
                  ) : (
                    <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                      <pre className="whitespace-pre-wrap text-sm text-gray-700">
                        {selectedLog.textBody || 'No preview available for this email.'}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default EmailLogs;
